// Credit: https://blog.logrocket.com/developing-responsive-layouts-with-react-hooks

import { createContext, useContext, useState, ReactNode, useMemo } from "react";
import { throttle } from "lodash";
import { Breakpoints, breakpoints } from "src/styles/theme";
import { useIsomorphicEffect } from "src/libs/nextLib";

type ViewportContextType = {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLessThanMedium: boolean;
  isLandscapeWindow: boolean;
};

const ViewportContext = createContext<ViewportContextType>({
  width: 0,
  height: 0,
  isMobile: true,
  isTablet: false,
  isDesktop: false,
  isLessThanMedium: false,
  isLandscapeWindow: false,
});

// The result of this function will only be useful if it's in pixels. It would
// not be a useful value if the unit is e.g. `%`, `em`, etc.
function stripUnit(value: string | number): number {
  if (typeof value === "number") {
    return value;
  }

  return parseFloat(value);
}

function getIsMobile(width: number, breakpoints: Breakpoints) {
  return width <= stripUnit(breakpoints.sm);
}

function getIsTablet(width: number, breakpoints: Breakpoints) {
  return (
    width > stripUnit(breakpoints.sm) && width <= stripUnit(breakpoints.lg)
  );
}

function getIsDesktop(width: number, breakpoints: Breakpoints) {
  return width > stripUnit(breakpoints.lg);
}

function getIsLessThanMedium(width: number, breakpoints: Breakpoints) {
  return width < stripUnit(breakpoints.md);
}

function getIsLandscapeWindow(height: number, width: number) {
  return height < width;
}

export const ViewportProvider = ({ children }: { children: ReactNode }) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useIsomorphicEffect(() => {
    // Set the size on the initial load. We need to do this here because we only
    // want it to run on the client.
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
    const handleWindowResize = throttle(
      () => {
        setWidth(window.innerWidth);
        setHeight(window.innerHeight);
      },
      250,
      { trailing: true }
    );

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const value = useMemo(
    () => ({
      width,
      height,
      isMobile: getIsMobile(width, breakpoints),
      isTablet: getIsTablet(width, breakpoints),
      isDesktop: getIsDesktop(width, breakpoints),
      isLessThanMedium: getIsLessThanMedium(width, breakpoints),
      isLandscapeWindow: getIsLandscapeWindow(height, width),
    }),
    [width, height]
  );
  return (
    <ViewportContext.Provider value={value}>
      {children}
    </ViewportContext.Provider>
  );
};

export const useViewport = () => {
  return useContext(ViewportContext);
};
